import * as React from 'react'
import {
  ChangeEvent,
  CompositionEvent,
  FC,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FieldProps, getIn } from 'formik'
import { debounce } from 'lodash'

import MuiTextField, { TextFieldProps } from '@mui/material/TextField'

type Props = Omit<TextFieldProps, 'name' | 'value' | 'error'> &
  FieldProps & {
    debounceDelay?: number
    avoidValidateOnBlur?: boolean
    charCountMax?: number
    // Shows the field in its error (red outline) state without any
    // helper text, independent of this field's own touched/error state —
    // for a validation rule reported at the group level (e.g. "First Name
    // or Last Name is required") rather than on any one field. Leaves the
    // normal touched-and-errored behavior untouched when omitted.
    forceError?: boolean
  }

// SH-537: some Android keyboards (Samsung's specifically) don't reliably
// enforce the native `maxLength` HTML attribute while composing text, so we
// can't rely on the browser alone to stop input at the limit.
export function truncateToMax(value: string, max?: number): string {
  return max != null ? value.slice(0, max) : value
}

export const DebouncedTextField: FC<Props> = (props) => {
  const {
    form: { isSubmitting, touched, errors },
    field,
    meta,
    debounceDelay = 300,
    helperText,
    size = 'small',
    margin = 'none',
    fullWidth = true,
    avoidValidateOnBlur = false,
    disabled = isSubmitting,
    onBlur: propsOnBlur = () => {},
    onChange: propsOnChange = () => {},
    charCountMax,
    forceError = false,
    ...textFieldProps
  } = props

  const { onChange, onBlur, ...rest } = field

  //#region value handling
  const [value, setValue] = useState(field.value)

  const update = useMemo(
    () => debounce(onChange, debounceDelay, { leading: true }),
    [debounceDelay, onChange],
  )

  const onChangeDebounced = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = truncateToMax(event.target.value?.trimStart(), charCountMax)
      // Keep the DOM value and the propagated event in sync with the
      // truncated value, since propsOnChange/update read event.target.value.
      event.target.value = value
      setValue(value)
      propsOnChange(event)
      update(event)
    },
    [propsOnChange, update, charCountMax],
  )

  // SH-537: on some Android keyboards, React doesn't reflect a truncated
  // controlled value back to the DOM while a word is still mid-composition,
  // so onChangeDebounced's trim only becomes visible once the field loses
  // focus. Re-running the same trim on compositionend forces the correction
  // at each word boundary instead (matches the ticket's expected behavior),
  // since composition has definitively ended by that point.
  const onCompositionEndTruncate = useCallback(
    (event: CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement
      const value = truncateToMax(target.value?.trimStart(), charCountMax)
      if (value !== target.value) {
        target.value = value
        setValue(value)
        // update/propsOnChange only ever read event.target, so hand them a
        // narrow object with just that, rather than casting the whole
        // CompositionEvent (which isn't actually a ChangeEvent) through
        // `unknown`.
        const changeEvent = {
          target,
        } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        propsOnChange(changeEvent)
        update(changeEvent)
      }
    },
    [propsOnChange, update, charCountMax],
  )

  const onBlurDebounced = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      update.flush()
      propsOnBlur(event)
      onBlur(event)
    },
    [onBlur, propsOnBlur, update],
  )
  useEffect(() => {
    if (typeof field.value === 'string') {
      setValue(field.value.trimStart())
    } else {
      setValue(field.value)
    }
  }, [field.value])
  //#endregion

  const errorText = getIn(errors, field.name)
  const error = forceError || (getIn(touched, field.name) && !!errorText)

  return (
    <MuiTextField
      {...textFieldProps}
      {...rest}
      value={value}
      onBlur={!avoidValidateOnBlur ? onBlurDebounced : undefined}
      onChange={onChangeDebounced}
      inputProps={{
        ...textFieldProps.inputProps,
        onCompositionEnd: (
          event: CompositionEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          // Compose with any consumer-supplied onCompositionEnd instead of
          // silently discarding it -- this is a shared component with many
          // consumers, even though none currently set this prop.
          textFieldProps.inputProps?.onCompositionEnd?.(
            event as CompositionEvent<HTMLInputElement>,
          )
          onCompositionEndTruncate(event)
        },
      }}
      error={error}
      helperText={
        error
          ? errorText
          : charCountMax
            ? `${(value?.length || 0).toLocaleString()}/${charCountMax}`
            : helperText
      }
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      margin={margin}
    />
  )
}
